import React, { useState, useEffect } from 'react';
import { 
  Zap, RefreshCw, Copy, Check, AlertCircle, CheckCircle2, 
  XCircle, Clock, Mail, Briefcase, MessageSquare, Eye,
  ChevronLeft, ChevronRight, Filter, Calendar, ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from './ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { toast } from '../hooks/use-toast';
import { zapierAPI } from '../api';

const ZapierConfig = () => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [copied, setCopied] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showLogDetail, setShowLogDetail] = useState(false);
  
  // Filtros
  const [filtroTipoPeticion, setFiltroTipoPeticion] = useState('');
  const [filtroTipoEmail, setFiltroTipoEmail] = useState('');
  const [filtroResultado, setFiltroResultado] = useState('');

  useEffect(() => {
    loadData();
  }, [page, filtroTipoPeticion, filtroTipoEmail, filtroResultado]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Cargar configuración y estadísticas en paralelo
      const [configRes, statsRes, logsRes] = await Promise.all([
        zapierAPI.getConfig(),
        zapierAPI.getStats(),
        zapierAPI.getLogs({
          page,
          page_size: pageSize,
          tipo_peticion: filtroTipoPeticion || undefined,
          tipo_email: filtroTipoEmail || undefined,
          resultado: filtroResultado || undefined
        })
      ]);
      
      setConfig(configRes.data);
      setStats(statsRes.data);
      setLogs(logsRes.data.logs || []);
      setTotalLogs(logsRes.data.total || 0);
    } catch (error) {
      console.error('Error cargando datos Zapier:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los datos de Zapier",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const copyApiKey = async () => {
    if (config?.api_key) {
      await navigator.clipboard.writeText(config.api_key);
      setCopied(true);
      toast({
        title: "Copiado",
        description: "API Key copiada al portapapeles"
      });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const testConnection = async () => {
    try {
      setTestingConnection(true);
      const response = await zapierAPI.testConexion();
      setConnectionStatus(response.data);
      
      if (response.data.status === 'ok') {
        toast({
          title: "Conexión exitosa",
          description: "La integración Zapier está funcionando correctamente"
        });
      } else {
        toast({
          title: "Error de conexión",
          description: response.data.mensaje,
          variant: "destructive"
        });
      }
    } catch (error) {
      setConnectionStatus({ status: 'error', mensaje: 'Error al probar la conexión' });
      toast({
        title: "Error",
        description: "No se pudo probar la conexión",
        variant: "destructive"
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const viewLogDetail = async (log) => {
    try {
      const response = await zapierAPI.getLogDetail(log.id);
      setSelectedLog(response.data);
      setShowLogDetail(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo cargar el detalle del log",
        variant: "destructive"
      });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(totalLogs / pageSize);

  if (loading && !config) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-orange-100 rounded-lg">
          <Zap className="h-6 w-6 text-orange-600" />
        </div>
        <div>
          <h2 className="text-xl font-semibold">Integración Zapier</h2>
          <p className="text-sm text-gray-500">
            Automatiza el procesamiento de emails de procuradores y clientes
          </p>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-blue-500" />
              <span className="text-sm text-gray-500">Total</span>
            </div>
            <p className="text-2xl font-bold">{stats?.total_peticiones || 0}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-green-500" />
              <span className="text-sm text-gray-500">Hoy</span>
            </div>
            <p className="text-2xl font-bold">{stats?.peticiones_hoy || 0}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              <span className="text-sm text-gray-500">Éxito</span>
            </div>
            <p className="text-2xl font-bold">{stats?.tasa_exito || 100}%</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-purple-500" />
              <span className="text-sm text-gray-500">Procurador</span>
            </div>
            <p className="text-2xl font-bold">{stats?.emails_procurador || 0}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-orange-500" />
              <span className="text-sm text-gray-500">Cliente</span>
            </div>
            <p className="text-2xl font-bold">{stats?.emails_cliente || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Configuración API Key */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Configuración de API</CardTitle>
          <CardDescription>
            Usa esta API Key para configurar tus Zaps en Zapier
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>API Key</Label>
            <div className="flex gap-2">
              <Input 
                type={showApiKey ? "text" : "password"}
                value={showApiKey ? config?.api_key : config?.api_key_masked}
                readOnly
                className="font-mono"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowApiKey(!showApiKey)}
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={copyApiKey}
              >
                {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={testConnection}
              disabled={testingConnection}
            >
              {testingConnection ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Zap className="h-4 w-4 mr-2" />
              )}
              Probar Conexión
            </Button>
            
            {connectionStatus && (
              <Badge variant={connectionStatus.status === 'ok' ? 'default' : 'destructive'}>
                {connectionStatus.status === 'ok' ? (
                  <><CheckCircle2 className="h-3 w-3 mr-1" /> Conectado</>
                ) : (
                  <><XCircle className="h-3 w-3 mr-1" /> Error</>
                )}
              </Badge>
            )}
          </div>

          {/* Endpoints disponibles */}
          <div className="mt-4">
            <Label className="mb-2 block">Endpoints disponibles</Label>
            <div className="space-y-2 text-sm">
              {config?.endpoints?.map((endpoint, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                  <Badge variant="outline" className="font-mono text-xs">
                    {endpoint.metodo}
                  </Badge>
                  <code className="text-xs flex-1">{endpoint.ruta}</code>
                  <span className="text-gray-500 text-xs">{endpoint.descripcion}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Header de autenticación:</strong> <code className="bg-blue-100 px-1 rounded">X-API-Key: {config?.api_key_masked}</code>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Logs de actividad */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Registro de Actividad</CardTitle>
              <CardDescription>
                Historial de emails procesados por Zapier
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={loadData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualizar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filtros */}
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="w-40">
              <Select value={filtroTipoPeticion || "all"} onValueChange={(v) => setFiltroTipoPeticion(v === "all" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Tipo peticion" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="procesar-email">Procesar Email</SelectItem>
                  <SelectItem value="crear-tarea-procurador">Crear Tarea</SelectItem>
                  <SelectItem value="crear-consulta-cliente">Crear Consulta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-40">
              <Select value={filtroTipoEmail || "all"} onValueChange={(v) => setFiltroTipoEmail(v === "all" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Tipo email" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="procurador">Procurador</SelectItem>
                  <SelectItem value="cliente">Cliente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-40">
              <Select value={filtroResultado || "all"} onValueChange={(v) => setFiltroResultado(v === "all" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Resultado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="exito">Exito</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tabla de logs */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]">Fecha</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Remitente</TableHead>
                  <TableHead>Asunto</TableHead>
                  <TableHead>Clasificación</TableHead>
                  <TableHead>Resultado</TableHead>
                  <TableHead className="w-[80px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      No hay registros de actividad
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-xs">
                        {formatDate(log.fecha)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {log.tipo_peticion === 'procesar-email' ? 'Clasificar' :
                           log.tipo_peticion === 'crear-tarea-procurador' ? 'Tarea' : 'Consulta'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm truncate max-w-[150px]">
                        {log.email_remitente || '-'}
                      </TableCell>
                      <TableCell className="text-sm truncate max-w-[200px]">
                        {log.email_asunto || '-'}
                      </TableCell>
                      <TableCell>
                        {log.tipo_email && (
                          <Badge 
                            variant={log.tipo_email === 'procurador' ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {log.tipo_email === 'procurador' ? (
                              <><Briefcase className="h-3 w-3 mr-1" /> Procurador</>
                            ) : (
                              <><MessageSquare className="h-3 w-3 mr-1" /> Cliente</>
                            )}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={log.resultado === 'exito' ? 'default' : 'destructive'}
                          className="text-xs"
                        >
                          {log.resultado === 'exito' ? (
                            <><CheckCircle2 className="h-3 w-3 mr-1" /> OK</>
                          ) : (
                            <><XCircle className="h-3 w-3 mr-1" /> Error</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => viewLogDetail(log)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">
                Mostrando {logs.length} de {totalLogs} registros
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>
                <span className="flex items-center px-3 text-sm">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Siguiente
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de detalle de log */}
      <Dialog open={showLogDetail} onOpenChange={setShowLogDetail}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalle del Registro</DialogTitle>
            <DialogDescription>
              Información completa de la petición procesada
            </DialogDescription>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Fecha</Label>
                  <p className="font-medium">{formatDate(selectedLog.fecha)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Tipo de Petición</Label>
                  <p className="font-medium">{selectedLog.tipo_peticion}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Remitente</Label>
                  <p className="font-medium">{selectedLog.email_remitente || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Clasificación</Label>
                  <Badge variant={selectedLog.tipo_email === 'procurador' ? 'default' : 'secondary'}>
                    {selectedLog.tipo_email || 'N/A'}
                  </Badge>
                </div>
                <div>
                  <Label className="text-gray-500">Resultado</Label>
                  <Badge variant={selectedLog.resultado === 'exito' ? 'default' : 'destructive'}>
                    {selectedLog.resultado}
                  </Badge>
                </div>
                <div>
                  <Label className="text-gray-500">Tiempo de Proceso</Label>
                  <p className="font-medium">{selectedLog.tiempo_procesamiento_ms || 0} ms</p>
                </div>
              </div>
              
              <div>
                <Label className="text-gray-500">Asunto</Label>
                <p className="font-medium">{selectedLog.email_asunto || '-'}</p>
              </div>
              
              {selectedLog.expediente_numero && (
                <div>
                  <Label className="text-gray-500">Expediente</Label>
                  <p className="font-medium">
                    {selectedLog.expediente_numero} (ID: {selectedLog.expediente_id})
                  </p>
                </div>
              )}
              
              {selectedLog.entidad_creada && (
                <div>
                  <Label className="text-gray-500">Entidad Creada</Label>
                  <p className="font-medium">
                    {selectedLog.entidad_creada} (ID: {selectedLog.entidad_id})
                  </p>
                </div>
              )}
              
              {selectedLog.mensaje_error && (
                <div>
                  <Label className="text-gray-500 text-red-600">Error</Label>
                  <p className="text-red-600 bg-red-50 p-2 rounded text-sm">
                    {selectedLog.mensaje_error}
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLogDetail(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ZapierConfig;
